import {
    type Flow,
    type AsyncFlow,
    type AsyncFlowState,
    createFlow,
    createAsyncFlow,
    memoizeFlowFactory,
} from '@adv-frontend/modules-flow';
import { stableSerialize } from '@direct-frontend/stdlib';
import { createEvent } from '@direct-frontend/modules-core';
import type {
    ApiEndpointQuery,
    QueryActionCreatorResult,
    QueryArgFrom,
    QueryResultSelectorResult,
    ResultTypeFrom,
    TagDescription,
} from '../rtk-query-types';
import type { GetStoreFn } from '../types';
import { createListenersCollection } from '../createListenersCollection';
import type { AnyApi } from '../buildCreateApi';
import { prefetchManager } from './prefetchManager';
import type { PrefetchKeyBuilder } from './buildEndpointPrefetch';
import type { AdditionalQueryState, AnyQueryDefinition, InferQueryDefinitionTagTypes } from './types';
import { refetchQueriesEvent } from './events';

/**
 * Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
 *
 * @typeParam Definition - Описание эндпоинта
 */
export type EndpointQueryFlow<Definition extends AnyQueryDefinition> = {
    /**
     * Создает поток для запроса с указанными аргументами и опциями
     *
     * @param queryArgs - Аргументы запроса
     * @param options - Опции выполнения запроса
     * @returns Поток для работы с запросом
     */
    queryFlow: QueryFlowFactory<Definition>;
};

/**
 * Создает поток для запроса с указанными аргументами и опциями
 *
 * @typeParam Definition - Описание эндпоинта
 * @param queryArgs - Аргументы запроса
 * @param options - Опции выполнения запроса
 * @returns Поток для работы с запросом
 */
type QueryFlowFactory<Definition extends AnyQueryDefinition> = (
    queryArgs: QueryArgFrom<Definition>,
    options?: QueryOptions
) => AsyncQueryFlow<Definition>;

/**
 * Параметры подписки на серверные данные
 */
interface QueryOptions {
    /**
     * Автоматическое обновление раз в N ms
     * @default undefined
     */
    pollingInterval?: number;
    /**
     * Данная настройка позволяет отключать автоматическое обновление данных, если окно не в фокусе.
     * @default false
     */
    skipPollingIfUnfocused?: boolean;
    /**
     * Автоматическое обновление при фокусе во вкладку
     * @default false
     */
    refetchOnFocus?: boolean;
    /**
     * Автоматическое обновление при появлении сети
     * @default false
     */
    refetchOnReconnect?: boolean;
    /**
     * Нужно ли отправлять запрос сразу при создании потока, чтобы предзагрузить данные.
     * Если false, то запрос уйдет только при появлении первой подписки на поток.
     * @default true
     */
    prefetch?: boolean;
    /**
     * Флаг, который позволяет сделать новый запрос даже в том случае, если в кеше есть данные.
     * @default false
     */
    forceRefetch?: boolean;
    /**
     * Возраст данных в кеше (в секундах), после которого нужно перезапросить данные.
     * Если в кеше есть достаточно свежие данные, то функция вернет закешированный результат без запроса в бекенд.
     * @default undefined
     */
    refetchIfOlderThan?: number;
    /**
     * Время в секундах, в течение которого храним подписку на данные внутри потока, даже если на поток никто не подписан.
     * Нужно для корректной работы Suspense, когда в endpoint отключено кеширование
     * @default 60
     */
    keepPrefetchedDataFor?: number;
}

/**
 * Информация о текущем запросе к эндпоинту
 *
 * @typeParam Definition - Описание эндпоинта
 */
interface QueryRequest<Definition extends AnyQueryDefinition> {
    /** ID последнего запроса */
    requestId: string;
    /** Время начала запроса в миллисекундах */
    startedTimestamp: number;
    /** Время окончания запроса в миллисекундах или null, если запрос еще не завершен */
    fulfilledTimestamp: number | null;
    /** Теги, которыми был инвалидирован последний запрос */
    invalidatedBy?: TagDescription<InferQueryDefinitionTagTypes<Definition>>[];
}

/**
 * Опции для метода flow.asPromise()
 */
interface AsPromiseOptions {
    /** Сигнал для отмены запроса */
    signal?: AbortSignal;
}

/**
 * Расширенный тип потока для запросов.
 * Совместим со всем стандартным туллингом для обычного AsyncFlow.
 * Также позволяет работать с результатом как с обычным промисом (для обратной совместимости).
 *
 * @typeParam Definition - Описание эндпоинта
 */
interface AsyncQueryFlow<Definition extends AnyQueryDefinition> extends AsyncFlow<ResultTypeFrom<Definition>> {
    /** Аргументы запроса, с которыми был создан поток */
    originalQueryArg: QueryArgFrom<Definition>;
    /** Информация о последнем выполненном запросе */
    currentRequest: Flow<QueryRequest<Definition> | null>;
    /** Перезапросить данные */
    refetch: () => void;
    /** Отменить активный запрос, если есть */
    abort: () => void;
    /** Расширенная функция asPromise, которая умеет принимать сигнал для отмены запроса */
    asPromise: (options?: AsPromiseOptions) => Promise<ResultTypeFrom<Definition>>;
}

/**
 * Параметры для создания фабрики потоков.
 */
type BuildQueryOptions = {
    /** Экземпляр API */
    api: AnyApi;
    /** Имя эндпоинта */
    endpointName: string;
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Функция для создания ключа для prefetchManager */
    buildPrefetchKey: PrefetchKeyBuilder;
};

/**
 * Создает функцию для построения потока запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param options - Опции для построения потока
 * @returns Функция для создания потока запроса
 */
export function buildEndpointQueryFlow<Definition extends AnyQueryDefinition>({
    api,
    endpointName,
    getStore,
    buildPrefetchKey,
}: BuildQueryOptions): QueryFlowFactory<Definition> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип эндпоинта заранее не известен
    const endpoint = api.endpoints[endpointName] as ApiEndpointQuery<Definition, {}>;

    const flowFactory = memoizeFlowFactory(
        (param: {
            queryArgs: QueryArgFrom<Definition>;
            queryOptions: QueryOptions;
            prefetchKey: string;
            cacheKey: string;
        }) => {
            return createQueryFlow({
                endpoint,
                queryArgs: param.queryArgs,
                options: param.queryOptions,
                getStore,
                prefetchKey: param.prefetchKey,
            });
        },
        {
            equals(a, b) {
                return a.cacheKey === b.cacheKey;
            },
            onCacheHit(flow) {
                flow.onCacheHit();
            },
        }
    );

    function query(queryArgs: QueryArgFrom<Definition>, queryOptions?: QueryOptions) {
        // Нормализуем типы в параметрах для эффективного кеширования инстансов
        const normalizedOptions: QueryOptions = {
            pollingInterval: queryOptions?.pollingInterval,
            skipPollingIfUnfocused: queryOptions?.skipPollingIfUnfocused ?? false,
            refetchOnFocus: queryOptions?.refetchOnFocus ?? false,
            refetchOnReconnect: queryOptions?.refetchOnReconnect ?? false,
            prefetch: queryOptions?.prefetch ?? true,
            forceRefetch: queryOptions?.forceRefetch ?? false,
            refetchIfOlderThan: queryOptions?.refetchIfOlderThan,
            keepPrefetchedDataFor: queryOptions?.keepPrefetchedDataFor ?? 60,
        };

        const prefetchKey = buildPrefetchKey(queryArgs);
        const cacheKey = `${prefetchKey},${stableSerialize(normalizedOptions)}`;

        return flowFactory({
            queryArgs,
            queryOptions: normalizedOptions,
            prefetchKey,
            cacheKey,
        }).flow;
    }

    return query;
}

/**
 * Опции для создания потока запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 */
interface CreateQueryFlowOptions<Definition extends AnyQueryDefinition> {
    /** Эндпоинт запроса */
    endpoint: ApiEndpointQuery<Definition, {}>;
    /** Аргументы запроса */
    queryArgs: QueryArgFrom<Definition>;
    /** Опции запроса */
    options?: QueryOptions;
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Ключ для prefetchManager */
    prefetchKey: string;
}

/**
 * Результат создания потока запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 */
interface CreateQueryFlowResult<Definition extends AnyQueryDefinition> {
    /** Созданный поток запроса */
    flow: AsyncQueryFlow<Definition>;
    /** Функция, вызываемая при попадании в кеш */
    onCacheHit: () => void;
}

/**
 * Создает поток для отслеживания состояния запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param options - Опции для создания потока
 * @returns Объект с потоком и функцией обработки попадания в кеш
 */
function createQueryFlow<Definition extends AnyQueryDefinition>({
    endpoint,
    queryArgs,
    options,
    getStore,
    prefetchKey,
}: CreateQueryFlowOptions<Definition>): CreateQueryFlowResult<Definition> {
    const dataSelector = endpoint.select(queryArgs);
    const getState = () => dataSelector(getStore().getState());

    const forceRefetch = options?.forceRefetch || options?.refetchIfOlderThan;
    const isPrefetchEnabled = options?.prefetch !== false;

    let querySubscription: QueryActionCreatorResult<Definition> | null = null;
    let refetchEventSubscription: (() => void) | null = null;
    let prevState = getState();
    let needRefetch = true;

    const asyncFlow = createAsyncFlow<ResultTypeFrom<Definition>>(mapQueryState(prevState));
    const currentRequest = createCurrentRequestFlow(getStore, getState);
    const activeQueries = new Set<QueryActionCreatorResult<Definition>>();

    const storeListener = createStoreListener({
        getStore,
        getState,
        equals(a, b) {
            return a.status === b.status && a.data === b.data && a.error === b.error;
        },
        onChange(state) {
            try {
                asyncFlow.emit(mapQueryState(state));
            } catch (err) {
                // Метод emit() проксирует ошибки от слушателей потока.
                // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                // поэтому ловим ошибки и просто их логируем
                console.error(err);
            }
        },
    });

    const listeners = createListenersCollection({
        onAdd(listener) {
            return asyncFlow.subscribe(listener);
        },
        onActivate() {
            createSubscriptions();
            return () => removeSubscriptions();
        },
    });

    /**
     * Подписывается на запрос в RTK Query для удержания данных в кеше.
     *
     * @returns Подписка на запрос
     */
    function subscribeToQuery() {
        const promise = getStore().dispatch(
            endpoint.initiate(queryArgs, {
                subscriptionOptions: {
                    pollingInterval: options?.pollingInterval,
                    skipPollingIfUnfocused: options?.skipPollingIfUnfocused,
                    refetchOnFocus: options?.refetchOnFocus,
                    refetchOnReconnect: options?.refetchOnReconnect,
                },
            })
        );
        trackQuery(promise);
        return promise;
    }

    /**
     * Подписывается на событие перезапроса данных.
     *
     * @returns Функция отписки от события
     */
    function subscribeToRefetchEvent() {
        return refetchQueriesEvent.subscribe(condition => {
            const queryState = getState();
            if (condition(queryState)) {
                flow.refetch();
            }
        });
    }

    /**
     * Создает все необходимые подписки для потока.
     */
    function createSubscriptions() {
        querySubscription = subscribeToQuery();
        refetchEventSubscription = subscribeToRefetchEvent();
        storeListener.subscribe();
        prefetchManager.remove(prefetchKey);
    }

    /**
     * Удаляет все подписки потока.
     */
    function removeSubscriptions() {
        querySubscription?.unsubscribe();
        querySubscription = null;
        refetchEventSubscription?.();
        refetchEventSubscription = null;
        storeListener.unsubscribe();
    }

    /**
     * Выполняет префетчинг данных, если это необходимо.
     */
    function prefetch() {
        needRefetch = false;

        const promise = getStore().dispatch(endpoint.initiate(queryArgs, { forceRefetch }));
        prefetchManager.set(prefetchKey, {
            promise,
            keepPrefetchedDataFor: options?.keepPrefetchedDataFor,
        });

        trackQuery(promise);
    }

    /**
     * Отслеживает активный запрос для управления его жизненным циклом.
     *
     * @param query - Запрос, который нужно отслеживать
     */
    function trackQuery(query: QueryActionCreatorResult<Definition>) {
        activeQueries.add(query);

        const onEnd = () => activeQueries.delete(query);
        query.then(onEnd, onEnd);
    }

    /**
     * Синхронизирует состояние потока с состоянием в store.
     */
    function syncFlowState() {
        const state = getState();

        if (state.isUninitialized || needRefetch) {
            prefetch();
        }

        storeListener.syncState();
    }

    /**
     * Пытается отменить запрос, если это возможно.
     *
     * @param currentQuery - Текущий запрос, который нужно отменить
     */
    function tryAbortQuery(currentQuery?: QueryActionCreatorResult<Definition>) {
        const prefetchQuery = prefetchManager.get(prefetchKey);
        let canAbort = true;

        for (const queries of activeQueries) {
            // Отменять запрос можем только в том случае, если нет активных подписок, кроме
            // prefetch запроса и подписки для текущего запроса, который хотим отменить
            if (queries !== currentQuery && queries !== prefetchQuery) {
                canAbort = false;
                break;
            }
        }

        if (canAbort) {
            prefetchQuery?.abort();
            currentQuery?.abort();
        }
    }

    /**
     * Обрабатывает попадание в кеш.
     */
    function onCacheHit() {
        if (isPrefetchEnabled) {
            prefetch();
        } else {
            needRefetch = true;
        }
    }

    const flow: AsyncQueryFlow<Definition> = {
        originalQueryArg: queryArgs,
        currentRequest,
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                syncFlowState();
            }

            return asyncFlow.getSnapshot();
        },
        asPromise(options) {
            if (options?.signal?.aborted) {
                tryAbortQuery();
                return Promise.reject(options.signal.reason);
            }

            if (!listeners.hasListeners()) {
                syncFlowState();
            }

            // сохраняем кеш запроса до момента резолва промиса
            const promiseQuerySubscription = getStore().dispatch(endpoint.initiate(queryArgs));
            trackQuery(promiseQuerySubscription);

            // отслеживаем изменения в сторе, чтобы зарезолвить промис
            const promiseStoreSubscription = getStore().subscribe(storeListener.syncState);

            const onEnd = () => {
                promiseQuerySubscription.unsubscribe();
                promiseStoreSubscription();
            };
            asyncFlow.asPromise().then(onEnd, onEnd);

            options?.signal?.addEventListener('abort', () => {
                tryAbortQuery(promiseQuerySubscription);
            });

            return asyncFlow.asPromise();
        },
        refetch() {
            const promise = getStore().dispatch(
                endpoint.initiate(queryArgs, {
                    forceRefetch: true,
                    subscribe: false,
                })
            );
            trackQuery(promise);
        },
        abort() {
            for (const query of activeQueries) {
                query.abort();
            }
        },
    };

    resetQueryFlows.subscribe(() => {
        listeners.reset();
        removeSubscriptions();
        flow.abort();
    });

    if (isPrefetchEnabled) {
        prefetch();
    }

    return { flow, onCacheHit };
}

/**
 * Преобразует состояние запроса из RTK Query в состояние AsyncFlow.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param state - Состояние запроса из RTK Query
 * @returns Состояние AsyncFlow
 */
function mapQueryState<Definition extends AnyQueryDefinition>(
    state: QueryResultSelectorResult<Definition>
): AsyncFlowState<ResultTypeFrom<Definition>> {
    if (state.status === 'fulfilled') {
        return { status: 'success', data: state.data };
    }

    if (state.status === 'rejected') {
        return { status: 'error', data: state.data, error: state.error };
    }

    return { status: 'pending', data: state.data };
}

/**
 * Создает поток для отслеживания информации о текущем запросе.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param getStore - Функция для получения store
 * @param getState - Функция для получения состояния запроса
 * @returns Поток с информацией о текущем запросе
 */
function createCurrentRequestFlow<Definition extends AnyQueryDefinition>(
    getStore: GetStoreFn,
    getState: () => QueryResultSelectorResult<Definition> & AdditionalQueryState<Definition>
): Flow<QueryRequest<Definition> | null> {
    const flow = createFlow<QueryRequest<Definition> | null>(mapQueryRequestState(getState()));

    const storeListener = createStoreListener({
        getStore,
        getState,
        equals(a, b) {
            return (
                a.requestId === b.requestId &&
                a.startedTimeStamp === b.startedTimeStamp &&
                a.fulfilledTimeStamp === b.fulfilledTimeStamp &&
                a.invalidatedBy === b.invalidatedBy
            );
        },
        onChange(state) {
            try {
                flow.emit(mapQueryRequestState(state));
            } catch (err) {
                // Метод emit() проксирует ошибки от слушателей потока.
                // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                // поэтому ловим ошибки и просто их логируем
                console.error(err);
            }
        },
    });

    const listeners = createListenersCollection({
        onAdd(listener) {
            return flow.subscribe(listener);
        },
        onActivate() {
            storeListener.subscribe();
            return () => storeListener.unsubscribe();
        },
    });

    resetQueryFlows.subscribe(() => {
        listeners.reset();
        storeListener.unsubscribe();
    });

    return {
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                storeListener.syncState();
            }

            return flow.getSnapshot();
        },
    };
}

/**
 * Преобразует состояние запроса в информацию о запросе.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param state - Состояние запроса из RTK Query
 * @returns Информация о запросе или null, если запрос не был начат
 */
function mapQueryRequestState<Definition extends AnyQueryDefinition>(
    state: QueryResultSelectorResult<Definition> & AdditionalQueryState<Definition>
): QueryRequest<Definition> | null {
    if (!state.requestId) {
        return null;
    }

    return {
        requestId: state.requestId,
        startedTimestamp: state.startedTimeStamp,
        fulfilledTimestamp: state.fulfilledTimeStamp ?? null,
        invalidatedBy: state.invalidatedBy,
    };
}

/**
 * Опции для создания слушателя изменений в store.
 *
 * @template T - Тип состояния
 */
interface StoreListenerOptions<T> {
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Функция для получения состояния */
    getState: () => T;
    /** Функция сравнения состояний */
    equals: (a: T, b: T) => boolean;
    /** Callback при изменении состояния */
    onChange: (state: T) => void;
}

/**
 * Создает слушатель изменений в store.
 *
 * @template T - Тип состояния
 * @param options - Опции слушателя
 * @returns Объект с методами управления подпиской
 */
export function createStoreListener<T>({ getStore, getState, equals, onChange }: StoreListenerOptions<T>) {
    let prevState = getState();
    let storeSubscription: (() => void) | null = null;

    function syncState() {
        const currentState = getState();

        if (!equals(currentState, prevState)) {
            prevState = currentState;
            onChange(currentState);
        }
    }

    return {
        syncState,
        subscribe() {
            return getStore().subscribe(syncState);
        },
        unsubscribe() {
            storeSubscription?.();
            storeSubscription = null;
        },
    };
}

/**
 * Событие, которое позволяет удалить подписки для всех потоков.
 * Используется для очистки состояния между тестами.
 * @internal
 */
export const resetQueryFlows = createEvent();
